/*
 * @Copyright 2023-2025. Institute for Future Intelligence, Inc.
 */

import { createWithEqualityFn } from 'zustand/traditional';
import { enableMapSet, produce } from 'immer';
import { devtools, persist, PersistStorage, StorageValue } from 'zustand/middleware';
import { Util } from '../Util';
import { VERSION } from '../constants';
import { Undoable } from '../undo/Undoable';
import { UndoManager } from '../undo/UndoManager';
import { ActionInfo, MoleculeInterface, ProjectInfo, ProjectState, RecentProjectInfo } from '../types';
import { Locale } from 'antd/lib/locale';
import enUS from 'antd/lib/locale/en_US';
import elementsUrl from '../assets/elements.csv';
import molecularPropertiesUrl from '../assets/molecular-properties.csv';
import { ChemicalElement } from '../models/ChemicalElement';
import Papa from 'papaparse';
import { MolecularProperties, MolecularStructure } from '../models/MolecularProperties';
import { CrystalProperties } from '../models/CrystalProperties';
import { CRYSTAL_PROPERTIES } from '../crystal/crystalProperties';
import { User } from '../User';
import { ProjectUtil } from '../project/ProjectUtil.ts';
import { Protein } from '../models/Protein.ts';
import { ModelUtil } from '../models/ModelUtil.ts';
import { Atom } from '../models/Atom.ts';
import { Restraint } from '../models/Restraint.ts';
import { Vector3 } from 'three';
import { Triple } from '../models/Triple.ts';
import { Quadruple } from '../models/Quadruple.ts';
import { usePrimitiveStore } from './commonPrimitive.ts';
import { setMessage } from '../helpers.tsx';
import i18n from '../i18n/i18n.ts';
import { rehydratePartialChargesFromSdf } from '../gasteigerCharges.ts';
import { rehydrateComputedPropertiesFromSdf } from '../computedProperties.ts';
import {
  migrateBiomoleculesField,
  migrateRetiredAiProvider,
  migrateUnnamedCrystals,
  fillEmptyLabFromGallery,
  padHiddenPropertiesForLegacyProjects,
} from '../project/ProjectUtil.ts';

// True if the URL carries `?template=...` from the case-studies "Try this
// idea" button. Used by the persist middleware below to skip restoring
// the previous project — the template loader then sets things up from a
// clean default state. Inlined here (rather than imported from
// landing/caseStudyTemplates) to avoid a circular module dependency
// during store init.
const hasTemplateUrlParam = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('template') !== null;
  } catch {
    return false;
  }
};

// ── Recent-projects / recent-examples storage ──────────────────────────────
// The "Recent Projects" submenu kept losing entries. Root cause: the recent
// lists used to ride the SAME localStorage key ('aims-storage') as the entire
// projectState. Two failure modes erased them:
//   1. skipHydration (opening via a shared-project URL or a case-study
//      template) starts the store with EMPTY recent lists; the next persist
//      write then overwrites the saved list with [].
//   2. A large projectState can exceed the ~5 MB localStorage quota, so the
//      whole blob — recent lists included — silently fails to write.
// Fix: persist the (tiny) recent lists in their OWN key, independent of the
// project blob, of skipHydration, and of the project's size. One-time migrate
// from the legacy combined blob on first read.
const RECENT_STORAGE_KEY = 'aims-recent';

const isViewOnly = (): boolean => {
  try {
    return typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('viewonly') === 'true';
  } catch {
    return false;
  }
};

interface RecentLists {
  projects: RecentProjectInfo[];
  examples: string[];
}

const readRecentLists = (): RecentLists => {
  const empty: RecentLists = { projects: [], examples: [] };
  try {
    if (typeof localStorage === 'undefined') return empty;
    const raw = localStorage.getItem(RECENT_STORAGE_KEY);
    if (raw) {
      const o = JSON.parse(raw);
      return {
        projects: Array.isArray(o?.projects) ? o.projects : [],
        examples: Array.isArray(o?.examples) ? o.examples : [],
      };
    }
    // One-time migration: pull whatever was last saved inside the legacy
    // combined blob so existing users don't see an empty list after the update.
    const legacy = localStorage.getItem('aims-storage');
    if (legacy) {
      const parsed = JSON.parse(legacy);
      const s = parsed?.state ?? parsed; // zustand persist wraps in { state, version }
      return {
        projects: Array.isArray(s?.recentlyOpenedProjects) ? s.recentlyOpenedProjects : [],
        examples: Array.isArray(s?.recentlyOpenedExamples) ? s.recentlyOpenedExamples : [],
      };
    }
  } catch {
    /* fall through to empty */
  }
  return empty;
};

const writeRecentLists = (projects: RecentProjectInfo[], examples: string[]): void => {
  // Don't pollute a viewer's recent list from a read-only shared link (the
  // old behavior routed viewonly persistence to sessionStorage).
  if (isViewOnly()) return;
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify({ projects, examples }));
  } catch {
    /* quota / disabled storage — recent lists are a convenience, ignore */
  }
};

// Seeded once at module load so the store starts with the persisted recent
// lists EVEN when the main project blob's hydration is skipped.
const initialRecentLists = readRecentLists();

// A localStorage write that exceeds the ~5 MB per-origin quota. Names/codes vary
// by browser (Chrome/Safari: 'QuotaExceededError' / code 22; Firefox:
// 'NS_ERROR_DOM_QUOTA_REACHED' / code 1014).
const isQuotaExceededError = (e: unknown): boolean => {
  const err = e as { name?: string; code?: number } | null;
  return (
    !!err &&
    (err.name === 'QuotaExceededError' ||
      err.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
      err.code === 22 ||
      err.code === 1014)
  );
};

// Free space held by OPTIONAL, fully-reconstructable localStorage caches so an
// unrelated project's auto-save can succeed. The RCSB PDB cache (`aims:pdb:*`,
// see pdbCache.ts) is cross-project and refetch-on-miss, so dropping it costs at
// most one network refetch — never data. This also self-heals users who still
// carry a legacy oversized PDB cache (the old 12 × 1 MB ceiling) from before the
// total-byte budget was added. Returns how many keys were removed. Prefixes are
// inlined rather than imported to avoid common.ts depending on the view layer.
const purgeRefetchableCaches = (): number => {
  let removed = 0;
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('aims:pdb:')) doomed.push(k);
    }
    for (const k of doomed) {
      try {
        localStorage.removeItem(k);
        removed++;
      } catch {
        /* ignore */
      }
    }
  } catch {
    /* localStorage unavailable — nothing to free */
  }
  return removed;
};

// The auto-save cache overflowing is not a lost-work event (the change is live in
// the session; it just won't survive a reload until the user frees space or saves
// to the cloud). Warn at most once a minute so a burst of writes can't spam.
let lastBrowserStorageWarnAt = 0;
const warnBrowserStorageFull = (): void => {
  const now = Date.now();
  if (now - lastBrowserStorageWarnAt < 60_000) return;
  lastBrowserStorageWarnAt = now;
  setMessage('warning', i18n.t('message.BrowserStorageFull', { lng: useStore.getState().language }), 12);
};

enableMapSet();

export interface CommonStoreState {
  set: (fn: (state: CommonStoreState) => void) => void;

  // only the following properties are persisted (see the whitelist at the end)
  version: string | undefined;
  language: string;
  locale: Locale;
  user: User;

  selectedFloatingWindow: string | null;

  // Gallery rendering mode: 3D viewer (Three.js) vs 2D structural sketch
  // (OpenChemLib SVG). Browser-persisted view preference, NOT part of
  // projectState — the choice travels with the user, not the project.
  gallerySketchMode: '3d' | '2d';
  setGallerySketchMode: (m: '3d' | '2d') => void;

  // Titles of the most-recently-opened built-in examples, most recent first.
  // Same persistence rationale as gallerySketchMode — a per-browser UI hint,
  // not part of any project. Capped to a handful (see examplesDrawer.tsx).
  recentlyOpenedExamples: string[];

  // The user's most-recently opened/saved cloud projects, most recent first,
  // capped to a handful (see useCloudProjects). Drives the "Recent Projects"
  // submenu in the File menu. Per-browser UI hint like recentlyOpenedExamples
  // — persisted, but not part of any project.
  recentlyOpenedProjects: RecentProjectInfo[];

  // Target of a pending "open a recent project" action, set by the File-menu
  // Recent Projects items and consumed in CloudManager (which has loadProject)
  // and in saveProject's save-then-open tail. Transient — not persisted.
  recentProjectToOpen: RecentProjectInfo | null;

  projectState: ProjectState;

  deleteAllAtoms: () => void;
  fixAtomByIndex: (index: number, fixed: boolean) => void;
  // `anchor`: where the spring is pinned when a restraint is CREATED — pass the
  // live (mirror) atom's position. The store atom's own position is the saved
  // frame, not the current one (a run moves atoms only in the engine; Save
  // levels the store), so anchoring there pegged the atom at a stale spot the
  // moment the engine was rebuilt from the store. Falls back to the store
  // position when no anchor is given (no engine mounted).
  restrainAtomByIndex: (index: number, strength: number, anchor?: { x: number; y: number; z: number }) => void;
  dampAtomByIndex: (index: number, damp: number) => void;
  chargeAtomByIndex: (index: number, charge: number, userSet?: boolean) => void;
  setAtomTrajectoryByIndex: (index: number, visible: boolean) => void;
  getAtomByIndex: (index: number) => Atom | null;

  // have to use objects as local storage does not accept maps
  angularBondsMap: { [key: string]: Triple[] };
  torsionalBondsMap: { [key: string]: Quadruple[] };

  proteinData: Protein | undefined;

  projectToOpen: ProjectInfo | null;

  hasMolecule: (molecule: MoleculeInterface) => boolean;
  addMolecule: (molecule: MoleculeInterface, index?: number) => boolean;
  addMolecules: (molecules: MoleculeInterface[]) => void;
  getMoleculeByName: (name: string) => MoleculeInterface | null;
  setMoleculeData: (name: string, data: string) => void;
  setMoleculeAutoBond: (name: string, autoBond: boolean) => void;
  renameMolecule: (oldName: string, newName: string) => boolean;
  removeMolecule: (molecule: MoleculeInterface) => void;
  removeMoleculeByName: (name: string) => void;
  removeAllMolecules: () => void;

  molecularPropertiesMap: Map<string, MolecularProperties>;
  setMolecularProperties: (name: string, properties: MolecularProperties) => void;
  molecularStructureMap: Map<string, MolecularStructure>;
  setMolecularStructure: (name: string, structure: MolecularStructure) => void;

  undoManager: UndoManager;
  addUndoable: (undoable: Undoable) => void;
  loggable: boolean;
  actionInfo: ActionInfo | undefined;
  logAction: (name: string) => void;
  currentUndoable: Undoable | undefined;

  chemicalElements: { [key: string]: ChemicalElement };
  getChemicalElement: (name: string) => ChemicalElement;
  loadChemicalElements: () => void;

  providedMolecularProperties: { [key: string]: MolecularProperties };
  getProvidedMolecularProperties: (name: string) => MolecularProperties;
  loadProvidedMolecularProperties: () => void;

  // Curated CRC/NIST property bundle for crystal presets. Keyed by preset
  // name (matching CrystalState.name). Custom / imported / AI-generated
  // crystals carry their own predicted values on the CrystalState itself
  // rather than appearing here — `getProvidedCrystalProperties` returns
  // undefined for unknown names and the caller falls back accordingly.
  providedCrystalProperties: { [key: string]: CrystalProperties };
  getProvidedCrystalProperties: (name: string) => CrystalProperties | undefined;
}

// The persisted slice of the store: what the browser auto-save keeps.
const persistedSnapshot = (state: CommonStoreState) => {
  const ps = state.projectState;
  // Clone testMolecules and strip DERIVED / TRANSIENT data without
  // mutating live state — everything dropped here is rebuilt on hydrate
  // by ModelUtil.reconstructMoleculesFromFirestore (the merge below runs
  // it, the same reconstruction a cloud load uses). This mirrors
  // stripProjectStateForSave in cloudProjectUtil.ts so the localStorage
  // round-trip is byte-for-byte equivalent to the Firestore one:
  //  • the three bond arrays are re-derived from the SDF at chamber load;
  //  • improperBonds hold four full Atom refs apiece (~43 KB/molecule) —
  //    the documented #1 cause of the blob blowing the ~5 MB quota — and
  //    are regenerated by Molecule.clone via buildMmffImpropers;
  //  • the transient per-atom physics fields (force / acceleration /
  //    displacement / initial* Vector3s, trajectory buffers, and the
  //    element-derived mass / sigma / epsilon / charge / index) are
  //    re-seeded from the SDF + element table on load.
  // Without this a busy Molecular-Modeling lab (many atoms, recorded
  // trajectories) overflows localStorage exactly like a big protein does.
  // Kept (unlike the Firestore strip): `charge`. It is one number per
  // atom (not a size driver) and this merge path rehydrates molecule-
  // level atomCharges but does not re-push them onto per-atom .charge,
  // so keeping it avoids any electrostatics regression after a reload.
  const ATOM_TRANSIENT_FIELDS = [
    'index',
    'mass',
    'sigma',
    'epsilon',
    'force',
    'acceleration',
    'displacement',
    'initialPosition',
    'initialVelocity',
    'trajectory',
  ] as const;
  const stripAtom = (a: any) => {
    const c = { ...a };
    for (const f of ATOM_TRANSIENT_FIELDS) delete c[f];
    return c;
  };
  const sanitizedTestMolecules = ps.testMolecules.map((m) => ({
    ...m,
    atoms: Array.isArray(m.atoms) ? m.atoms.map(stripAtom) : m.atoms,
    radialBonds: [],
    angularBonds: [],
    torsionalBonds: [],
    improperBonds: [],
  }));
  // Drop the inline PDB text of URL-backed biomolecules from the
  // localStorage auto-save — a single large protein is 100 KB–several MB
  // of coordinates, and a few of them blow the ~5 MB per-origin quota
  // (the Protein / DNA-RNA Labs' main storage risk). bioStructureViewer
  // re-fetches `data` from `url` on demand, so rendering/analysis are
  // unaffected after a reload. BUILT / FOLDED / MUTATED structures have
  // no url (their text is the only copy) and are small (≤400 res), so
  // they keep their data. Mirrors stripProjectStateForSave for Firestore
  // (cloudProjectUtil.ts) — done here without mutating live state.
  const stripBio = <T extends { data?: string; url?: string }>(b: T): T =>
    b?.data && b?.url ? { ...b, data: undefined } : b;
  const sanitizedProjectState = {
    ...ps,
    testMolecules: sanitizedTestMolecules,
    ...(ps.biomolecules ? { biomolecules: ps.biomolecules.map(stripBio) } : {}),
    ...(ps.biomolecule ? { biomolecule: stripBio(ps.biomolecule) } : {}),
  };
  return {
    language: state.language,
    user: state.user,
    projectState: sanitizedProjectState,
    selectedFloatingWindow: state.selectedFloatingWindow,
    gallerySketchMode: state.gallerySketchMode,
    // recentlyOpenedProjects / recentlyOpenedExamples are intentionally
    // NOT persisted here — they live in their own 'aims-recent' key so
    // a skipped hydration or an over-quota project blob can't erase
    // them (see readRecentLists / the subscribe below).
    angularBondsMap: state.angularBondsMap,
    torsionalBondsMap: state.torsionalBondsMap,
  };
};

// Browser auto-save, coalesced. The persist middleware writes on EVERY set, and a
// write clones each lab molecule, strips its atoms, stringifies the whole project
// and stores it synchronously — a cost that grows with the lab, paid per set. A
// load that set the store once per molecule (255 argon atoms → ~510 sets) spent
// seconds here with the canvas blank. Now a set only records the latest state;
// the write runs at most every PERSIST_WRITE_DELAY_MS with whatever is newest,
// and immediately when the page is hidden or unloads, so nothing is lost on a
// reload or tab close. A quota overflow can no longer throw out of set(), so the
// same self-heal (free refetchable caches, retry once, then warn) runs here.
const PERSIST_WRITE_DELAY_MS = 400;
const persistBackend = (): Storage | null => {
  try {
    const viewOnly = new URLSearchParams(window.location.search).get('viewonly') === 'true';
    return viewOnly ? sessionStorage : localStorage;
  } catch {
    return null;
  }
};
let pendingPersist: { name: string; value: StorageValue<CommonStoreState> } | null = null;
let persistTimer: ReturnType<typeof setTimeout> | null = null;
const flushPersist = (): void => {
  if (persistTimer !== null) {
    clearTimeout(persistTimer);
    persistTimer = null;
  }
  const pending = pendingPersist;
  pendingPersist = null;
  const backend = persistBackend();
  if (!pending || !backend) return;
  let text: string;
  try {
    text = JSON.stringify({ state: persistedSnapshot(pending.value.state), version: pending.value.version });
  } catch (e) {
    console.error('common store auto-save failed', e);
    return;
  }
  try {
    backend.setItem(pending.name, text);
  } catch (e) {
    if (!isQuotaExceededError(e)) {
      console.error('common store auto-save failed', e);
      return;
    }
    if (purgeRefetchableCaches() > 0) {
      try {
        backend.setItem(pending.name, text);
        return;
      } catch (e2) {
        if (!isQuotaExceededError(e2)) {
          console.error('common store auto-save failed', e2);
          return;
        }
      }
    }
    warnBrowserStorageFull();
  }
};
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushPersist);
  window.addEventListener('beforeunload', flushPersist);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushPersist();
  });
}
const deferredPersistStorage: PersistStorage<CommonStoreState> = {
  getItem: (name) => {
    if (pendingPersist?.name === name) flushPersist();
    const raw = persistBackend()?.getItem(name);
    return raw ? (JSON.parse(raw) as StorageValue<CommonStoreState>) : null;
  },
  setItem: (name, value) => {
    pendingPersist = { name, value };
    if (persistTimer === null) persistTimer = setTimeout(flushPersist, PERSIST_WRITE_DELAY_MS);
  },
  removeItem: (name) => {
    if (pendingPersist?.name === name) pendingPersist = null;
    persistBackend()?.removeItem(name);
  },
};

export const useStore = createWithEqualityFn<CommonStoreState>()(
  devtools(
    persist(
      (set, get) => {
        const immerSet: CommonStoreState['set'] = (fn) => set(produce(fn));

        return {
          set: (fn) => {
            try {
              immerSet(fn);
            } catch (e) {
              // The in-memory update is applied BEFORE the persist middleware
              // writes to localStorage, so a quota overflow here means the
              // change (e.g. a freshly loaded protein) IS live in the session —
              // only the browser auto-save cache failed. Surface that accurately
              // (throttled) instead of a scary generic error, so the agent /
              // user don't mistake it for a failed load.
              if (isQuotaExceededError(e)) {
                // Self-heal: the quota is shared across the whole origin, so the
                // culprit is often the cross-project PDB cache (or a legacy
                // oversized one) rather than this project's own data. Free that
                // refetchable space and retry the write ONCE before warning.
                if (purgeRefetchableCaches() > 0) {
                  try {
                    immerSet(fn);
                    return;
                  } catch (e2) {
                    if (!isQuotaExceededError(e2)) {
                      console.error('common store set failed', e2);
                      return;
                    }
                    // Still over quota after freeing the caches — fall through.
                  }
                }
                warnBrowserStorageFull();
              } else {
                console.error('common store set failed', e);
              }
            }
          },
          version: VERSION,
          language: 'en',
          locale: enUS,
          user: {} as User,

          selectedFloatingWindow: null,

          gallerySketchMode: '3d',
          setGallerySketchMode(m: '3d' | '2d') {
            immerSet((state: CommonStoreState) => {
              state.gallerySketchMode = m;
            });
          },

          // Seeded from the dedicated 'aims-recent' key (see readRecentLists)
          // so they survive even when the main project blob is not hydrated.
          recentlyOpenedExamples: initialRecentLists.examples,
          recentlyOpenedProjects: initialRecentLists.projects,
          recentProjectToOpen: null,

          projectState: ProjectUtil.createDefaultProjectState(),

          deleteAllAtoms() {
            immerSet((state: CommonStoreState) => {
              state.projectState.testMolecules = [];
            });
            usePrimitiveStore.getState().set((state) => {
              state.pickedMoleculeIndex = -1;
              state.pickedAtomIndex = -1;
              state.startSimulation = false;
            });
          },
          fixAtomByIndex(index: number, fixed: boolean) {
            immerSet((state: CommonStoreState) => {
              let i = 0;
              loop1: for (const m of state.projectState.testMolecules) {
                for (const a of m.atoms) {
                  if (i === index) {
                    a.fixed = fixed;
                    break loop1;
                  }
                  i++;
                }
              }
            });
          },
          restrainAtomByIndex(index: number, strength: number, anchor?: { x: number; y: number; z: number }) {
            immerSet((state: CommonStoreState) => {
              let i = 0;
              loop1: for (const m of state.projectState.testMolecules) {
                for (const a of m.atoms) {
                  if (i === index) {
                    if (a.restraint) {
                      if (strength > 0) {
                        a.restraint.strength = strength;
                      } else {
                        a.restraint = undefined;
                      }
                    } else {
                      if (strength > 0) {
                        const p = anchor ? new Vector3(anchor.x, anchor.y, anchor.z) : a.position.clone();
                        a.restraint = new Restraint(strength, p);
                      }
                    }
                    break loop1;
                  }
                  i++;
                }
              }
            });
          },
          dampAtomByIndex(index: number, damp: number) {
            immerSet((state: CommonStoreState) => {
              let i = 0;
              loop1: for (const m of state.projectState.testMolecules) {
                for (const a of m.atoms) {
                  if (i === index) {
                    a.damp = damp;
                    break loop1;
                  }
                  i++;
                }
              }
            });
          },
          // userSet false clears the user-override flag: an undo putting back a
          // computed charge must leave it computed (re-derived on load / rebuild).
          chargeAtomByIndex(index: number, charge: number, userSet = true) {
            immerSet((state: CommonStoreState) => {
              let i = 0;
              loop1: for (const m of state.projectState.testMolecules) {
                for (const a of m.atoms) {
                  if (i === index) {
                    a.charge = charge;
                    if (userSet)
                      a.chargeSet = true; // a user override (see Atom.chargeSet)
                    else delete a.chargeSet;
                    break loop1;
                  }
                  i++;
                }
              }
            });
          },
          setAtomTrajectoryByIndex(index: number, visible: boolean) {
            immerSet((state: CommonStoreState) => {
              let i = 0;
              loop1: for (const m of state.projectState.testMolecules) {
                for (const a of m.atoms) {
                  if (i === index) {
                    a.trajectory = visible;
                    break loop1;
                  }
                  i++;
                }
              }
            });
          },
          getAtomByIndex(index: number) {
            let atom = null;
            immerSet((state: CommonStoreState) => {
              let i = 0;
              loop1: for (const m of state.projectState.testMolecules) {
                for (const a of m.atoms) {
                  if (i === index) {
                    atom = a;
                    break loop1;
                  }
                  i++;
                }
              }
            });
            return atom;
          },

          angularBondsMap: {},
          torsionalBondsMap: {},

          proteinData: undefined,

          projectToOpen: null,

          hasMolecule(molecule: MoleculeInterface) {
            let found = false;
            immerSet((state: CommonStoreState) => {
              for (const m of state.projectState.molecules) {
                if (m.name === molecule.name) {
                  found = true;
                  break;
                }
              }
            });
            return found;
          },
          addMolecule(molecule: MoleculeInterface, index?: number) {
            // Operate on a shallow COPY. The charge rehydration + timestamp
            // stamp below mutate top-level fields, and the incoming molecule
            // may be FROZEN — Immer auto-freezes state (autoFreeze is on), so
            // an undo re-adding a molecule captured from prior state hands us a
            // frozen object. Mutating it throws, which silently aborts the
            // re-add — the bug behind "undo shows a name but restores nothing".
            // (The chamber-side delete undo avoids this by capturing a
            // Molecule.clone up front; the gallery path re-added the original.)
            const mol: MoleculeInterface = { ...molecule };
            // Make sure every molecule entering the gallery has partial
            // charges, regardless of source (internal DB, drag-drop, undo).
            // No-op when charges are already present.
            rehydratePartialChargesFromSdf([mol]);
            // Stamp a creation time once, so the chat-view conversation
            // timeline can interleave this molecule with Q&A messages in the
            // order it was actually created. Preserve an existing stamp (e.g.
            // an undo re-adding a molecule keeps its original position).
            if (mol.timestamp === undefined) mol.timestamp = new Date().getTime();
            let added = true;
            immerSet((state: CommonStoreState) => {
              for (const m of state.projectState.molecules) {
                if (m.name === mol.name) {
                  added = false;
                  break;
                }
              }
              if (added) {
                if (index === undefined) {
                  state.projectState.molecules.push(mol);
                } else {
                  if (index >= 0 && index <= state.projectState.molecules.length) {
                    state.projectState.molecules.splice(index, 0, mol);
                  }
                }
              }
            });
            if (added) {
              usePrimitiveStore.getState().set((s) => {
                s.latestAddedMoleculeName = mol.name;
              });
              // Mark the project dirty at the mutation site, atomically with the
              // add — the same way addBiomoleculeStructure self-marks. Previously
              // this relied on each of the ~10 callers (chat pipelines, import,
              // docking-pocket, scissor) calling setChanged separately AFTER the
              // add; a throw/abort in between (e.g. property computation) left the
              // molecule added but the project looking clean, and any new caller
              // could forget entirely. setChanged() no-ops in view-only mode.
              usePrimitiveStore.getState().setChanged(true);
            }
            return added;
          },
          addMolecules(molecules: MoleculeInterface[]) {
            // Shallow-copy each so the rehydrate + timestamp stamp never mutate
            // a frozen input (e.g. an undo re-adding captured molecules). See
            // the note in addMolecule.
            const mols: MoleculeInterface[] = molecules.map((m) => ({ ...m }));
            rehydratePartialChargesFromSdf(mols);
            const now = new Date().getTime();
            for (const molecule of mols) {
              if (molecule.timestamp === undefined) molecule.timestamp = now;
            }
            let lastAdded: string | null = null;
            immerSet((state: CommonStoreState) => {
              const m2: MoleculeInterface[] = [];
              for (const molecule of mols) {
                let included = false;
                for (const m of state.projectState.molecules) {
                  if (m.name === molecule.name) {
                    included = true;
                    break;
                  }
                }
                if (!included) {
                  m2.push(molecule);
                }
              }
              if (m2.length > 0) {
                state.projectState.molecules.push(...m2);
                lastAdded = m2[m2.length - 1].name;
              }
            });
            if (lastAdded !== null) {
              const name: string = lastAdded;
              usePrimitiveStore.getState().set((s) => {
                s.latestAddedMoleculeName = name;
              });
              // Self-mark dirty at the mutation site (see addMolecule).
              usePrimitiveStore.getState().setChanged(true);
            }
          },
          getMoleculeByName(name: string) {
            let mol = null;
            for (const m of get().projectState.molecules) {
              if (m.name === name) {
                mol = m;
                break;
              }
            }
            return mol;
          },
          setMoleculeData(name: string, data: string) {
            immerSet((state: CommonStoreState) => {
              for (const m of state.projectState.molecules) {
                if (m.name === name) {
                  m.data = data;
                  usePrimitiveStore.getState().set((state) => {
                    state.moleculeDataUpdated = true;
                  });
                  break;
                }
              }
            });
          },
          setMoleculeAutoBond(name: string, autoBond: boolean) {
            immerSet((state: CommonStoreState) => {
              for (const m of state.projectState.molecules) {
                if (m.name === name) {
                  m.autoBond = autoBond;
                  break;
                }
              }
            });
          },
          renameMolecule(oldName: string, newName: string): boolean {
            const trimmed = newName.trim();
            if (!trimmed || trimmed === oldName) return false;
            // Read-side uniqueness check before mutating, so a conflict aborts
            // cleanly without partially renaming related state.
            const current = useStore.getState().projectState.molecules;
            if (current.some((m) => m.name === trimmed)) return false;
            immerSet((state: CommonStoreState) => {
              for (const m of state.projectState.molecules) {
                if (m.name === oldName) m.name = trimmed;
              }
              if (state.projectState.selectedMolecule?.name === oldName) {
                state.projectState.selectedMolecule.name = trimmed;
              }
              const props = state.projectState.generatedMolecularProperties[oldName];
              if (props !== undefined) {
                delete state.projectState.generatedMolecularProperties[oldName];
                state.projectState.generatedMolecularProperties[trimmed] = props;
              }
            });
            return true;
          },
          removeMolecule(molecule: MoleculeInterface) {
            immerSet((state: CommonStoreState) => {
              for (const [i, m] of state.projectState.molecules.entries()) {
                if (m.name === molecule.name) {
                  state.projectState.molecules.splice(i, 1);
                  break;
                }
              }
            });
          },
          removeMoleculeByName(name: string) {
            immerSet((state: CommonStoreState) => {
              for (const [i, m] of state.projectState.molecules.entries()) {
                if (m.name === name) {
                  state.projectState.molecules.splice(i, 1);
                  break;
                }
              }
            });
          },
          removeAllMolecules() {
            immerSet((state: CommonStoreState) => {
              state.projectState.generatedMolecularProperties = {};
              state.projectState.molecules = [];
              state.projectState.selectedMolecule = null;
            });
          },

          molecularPropertiesMap: new Map<string, MolecularProperties>(),
          setMolecularProperties(name: string, properties: MolecularProperties) {
            immerSet((state: CommonStoreState) => {
              state.molecularPropertiesMap.set(name, properties);
            });
          },
          molecularStructureMap: new Map<string, MolecularStructure>(),
          setMolecularStructure(name: string, structure: MolecularStructure) {
            immerSet((state: CommonStoreState) => {
              state.molecularStructureMap.set(name, structure);
            });
          },

          undoManager: new UndoManager(),
          addUndoable(undoable: Undoable) {
            if (!usePrimitiveStore.getState().muteUndoMessage) {
              setMessage('undo', undoable.name);
            }
            immerSet((state: CommonStoreState) => {
              if (state.loggable) {
                state.currentUndoable = undoable;
              }
              state.undoManager.add(undoable);
            });
          },
          loggable: false,
          actionInfo: undefined,
          logAction(name: string) {
            immerSet((state: CommonStoreState) => {
              state.actionInfo = { name, timestamp: new Date().getTime() };
            });
          },
          currentUndoable: undefined,

          chemicalElements: {},
          getChemicalElement(name: string) {
            return get().chemicalElements[name];
          },
          loadChemicalElements() {
            Papa.parse(elementsUrl, {
              download: true,
              complete: function (results) {
                for (const token of results.data) {
                  if (Array.isArray(token) && token.length > 1) {
                    if (token[0] !== 'AtomicNumber') {
                      const element = {
                        atomicNumber: parseInt(token[0].trim()),
                        symbol: token[1].trim(),
                        name: token[2].trim(),
                        atomicMass: parseFloat(token[3].trim()),
                        cpkHexColor: token[4].trim(),
                        electronConfiguration: token[5].trim(),
                        electronegativity: parseFloat(token[6].trim()),
                        atomicRadius: parseFloat(token[7].trim()) * 0.01, // angstrom (100 pm)
                        ionizationEnergy: parseFloat(token[8].trim()),
                        electronAffinity: parseFloat(token[9].trim()),
                      } as ChemicalElement;
                      immerSet((state: CommonStoreState) => {
                        state.chemicalElements[element.symbol] = element;
                      });
                    }
                  }
                }
              },
            });
          },

          // Static crystal-properties table — no async load, the values are
          // compiled into the bundle from crystalProperties.ts.
          providedCrystalProperties: CRYSTAL_PROPERTIES,
          getProvidedCrystalProperties(name: string) {
            return get().providedCrystalProperties[name];
          },

          providedMolecularProperties: {},
          getProvidedMolecularProperties(name: string) {
            return get().providedMolecularProperties[name];
          },
          loadProvidedMolecularProperties() {
            Papa.parse(molecularPropertiesUrl, {
              download: true,
              complete: function (results) {
                for (const token of results.data) {
                  if (Array.isArray(token) && token.length > 1) {
                    if (token[0] !== 'Name') {
                      const molProp = {
                        formula: token[1].trim(),
                        molecularMass: parseFloat(token[2].trim()),
                        logP: parseFloat(token[3].trim()),
                        hydrogenBondDonorCount: parseInt(token[4].trim()),
                        hydrogenBondAcceptorCount: parseInt(token[5].trim()),
                        rotatableBondCount: parseInt(token[6].trim()),
                        polarSurfaceArea: parseFloat(token[7].trim()),
                        heavyAtomCount: parseFloat(token[8].trim()),
                        complexity: parseFloat(token[9].trim()),
                        density: token[10].trim() === 'NA' ? Number.NaN : parseFloat(token[10].trim()),
                        boilingPoint: token[11].trim() === 'NA' ? Number.NaN : parseFloat(token[11].trim()),
                        meltingPoint: token[12].trim() === 'NA' ? Number.NaN : parseFloat(token[12].trim()),
                        smiles: token[13]?.trim(),
                        inChI: token[14]?.trim(),
                      } as MolecularProperties;
                      immerSet((state: CommonStoreState) => {
                        state.providedMolecularProperties[token[0].trim()] = molProp;
                      });
                    }
                  }
                }
              },
            });
          },
        };
      },
      {
        name: 'aims-storage',
        storage: deferredPersistStorage,
        // Skip restoring the persisted project when:
        //  - the URL identifies a shared project (userid + project params), OR
        //  - the URL carries a starting template (template param) — the user
        //    explicitly picked a fresh case-study setup on /case-studies, and
        //    restoring the previous project would surface stale title / camera
        //    / chamber settings in the new chamber.
        skipHydration: Util.isOpenFromURL() || hasTemplateUrlParam(),
        // Identity: the strip runs in deferredPersistStorage's write, not on every set.
        partialize: (state) => state,
        merge: (persistedState: any, currentState: CommonStoreState) => {
          const state = { ...currentState, ...persistedState } as CommonStoreState;
          // The recent lists now live in their own key. A legacy combined blob
          // may still carry a (now stale) copy — keep the freshly-seeded values
          // from currentState so the blob can't override 'aims-recent'.
          state.recentlyOpenedProjects = currentState.recentlyOpenedProjects;
          state.recentlyOpenedExamples = currentState.recentlyOpenedExamples;
          state.projectState.testMolecules = ModelUtil.reconstructMoleculesFromFirestore(
            state.projectState.testMolecules,
          );
          // Restore atomCharges from SDF when only the SDF copy survived the
          // round-trip. Skipped for molecules that already have atomCharges.
          rehydratePartialChargesFromSdf(state.projectState.molecules);
          rehydratePartialChargesFromSdf(state.projectState.testMolecules);
          // Recompute structural / descriptor / drug-likeness for generated
          // molecules — projects persisted before client-side computation
          // are missing the pass flags (and may carry AI-fabricated values).
          rehydrateComputedPropertiesFromSdf(
            state.projectState.molecules,
            state.projectState.generatedMolecularProperties,
          );
          rehydrateComputedPropertiesFromSdf(
            state.projectState.testMolecules,
            state.projectState.generatedMolecularProperties,
          );
          // One-shot migration: hide the four newer structurally-derived
          // descriptors on legacy projects that predate the feature.
          padHiddenPropertiesForLegacyProjects(state.projectState);
          // One-shot migration: fold a legacy single `biomolecule` into the
          // `biomolecules` collection (labs became multi-structure).
          migrateBiomoleculesField(state.projectState);
          // One-shot migration: retired AI tiers (notably DeepSeek's v4-pro)
          // become the tier that actually runs.
          migrateRetiredAiProvider(state.projectState);
          // Unnamed crystals (older projects) get a name, or nothing can
          // delete or rename them.
          migrateUnnamedCrystals(state.projectState);
          // A crystal-core project with gallery structures never opens with
          // an empty lab.
          fillEmptyLabFromGallery(state.projectState, state.projectState.type);
          // A plate synced at a PAUSE does not match the atoms persisted here
          // (they were last synced at save/load), so the reloaded lab would put
          // gas above the plate — restore the plate that matches those atoms.
          const snap = state.projectState.pistonSnapshot;
          if (snap && snap.reloadBaseline !== undefined) {
            state.projectState = {
              ...state.projectState,
              pistonSnapshot: snap.reloadBaseline ? { ...snap.reloadBaseline } : null,
            };
          }
          return state;
        },
      },
    ),
  ),
);

// Persist the recent lists to their own dedicated key whenever they change.
// Decoupled from the main project blob so they can't be wiped by a skipped
// hydration or an over-quota projectState (see readRecentLists above).
useStore.subscribe((state, prevState) => {
  if (
    state.recentlyOpenedProjects !== prevState.recentlyOpenedProjects ||
    state.recentlyOpenedExamples !== prevState.recentlyOpenedExamples
  ) {
    writeRecentLists(state.recentlyOpenedProjects, state.recentlyOpenedExamples);
  }
});
